import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';

const BACKEND_URL = getBackendUrl();

const SongIdentityManager = () => {
  // Global Stats
  const [globalStats, setGlobalStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  
  // Selected Song Details
  const [selectedSong, setSelectedSong] = useState(null);
  const [loadingSongDetails, setLoadingSongDetails] = useState(false);
  
  // Backfill
  const [backfillResult, setBackfillResult] = useState(null);
  const [loadingBackfill, setLoadingBackfill] = useState(false);
  
  // MERGE STATE - Multi-source support
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSources, setMergeSources] = useState([]); // Array of songs to merge FROM
  const [mergeTarget, setMergeTarget] = useState(null); // Single song to merge INTO
  const [mergePreview, setMergePreview] = useState(null);
  const [loadingMerge, setLoadingMerge] = useState(false);
  const [mergeSuccess, setMergeSuccess] = useState(null);
  const [customDisplayArtist, setCustomDisplayArtist] = useState('');
  const [customDisplayTitle, setCustomDisplayTitle] = useState('');
  
  const [error, setError] = useState('');

  const getAuthHeaders = useCallback(() => {
    const token = storage.getItem('token');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  }, []);

  // Fetch global stats
  const fetchGlobalStats = useCallback(async () => {
    setLoadingStats(true);
    setError('');
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/stream-monitor/song-identity/stats`,
        getAuthHeaders()
      );
      setGlobalStats(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else {
        setError(err.response?.data?.detail || 'Error loading stats');
      }
    } finally {
      setLoadingStats(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchGlobalStats();
  }, [fetchGlobalStats]);

  // Search songs
  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery || searchQuery.length < 2) {
      setError('Εισάγετε τουλάχιστον 2 χαρακτήρες για αναζήτηση');
      return;
    }
    
    setLoadingSearch(true);
    setError('');
    setSelectedSong(null);
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/stream-monitor/song-identity/search`,
        {
          ...getAuthHeaders(),
          params: { q: searchQuery, limit: 50 }
        }
      );
      setSearchResults(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error searching');
    } finally {
      setLoadingSearch(false);
    }
  };

  // Get song details by song_uid
  const fetchSongDetails = async (songUid) => {
    setLoadingSongDetails(true);
    setError('');
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/stream-monitor/song-identity/${songUid}`,
        getAuthHeaders()
      );
      setSelectedSong(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading song details');
    } finally {
      setLoadingSongDetails(false);
    }
  };

  // Run backfill (dry run first)
  const handleBackfill = async (dryRun = true) => {
    if (!dryRun && !window.confirm('Θέλετε σίγουρα να εκτελέσετε το backfill; Αυτό θα δημιουργήσει song_uid για όλα τα records χωρίς.')) {
      return;
    }
    
    setLoadingBackfill(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/stream-monitor/song-identity/backfill`,
        null,
        {
          ...getAuthHeaders(),
          params: { dry_run: dryRun, batch_size: 1000 }
        }
      );
      setBackfillResult(response.data);
      
      // Refresh stats after actual backfill
      if (!dryRun) {
        await fetchGlobalStats();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error running backfill');
    } finally {
      setLoadingBackfill(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // MERGE FUNCTIONS - Multi-source support
  // ═══════════════════════════════════════════════════════════════
  
  // Start merge mode
  const startMergeMode = () => {
    setMergeMode(true);
    setMergeSources([]);
    setMergeTarget(null);
    setMergePreview(null);
    setMergeSuccess(null);
    setCustomDisplayArtist('');
    setCustomDisplayTitle('');
    setError('');
  };
  
  // Cancel merge mode
  const cancelMerge = () => {
    setMergeMode(false);
    setMergeSources([]);
    setMergeTarget(null);
    setMergePreview(null);
    setMergeSuccess(null);
  };
  
  // Toggle source selection (checkbox)
  const toggleSource = (song) => {
    const isSelected = mergeSources.some(s => s.song_uid === song.song_uid);
    
    if (isSelected) {
      setMergeSources(mergeSources.filter(s => s.song_uid !== song.song_uid));
    } else {
      // Don't add if it's the target
      if (mergeTarget?.song_uid === song.song_uid) return;
      setMergeSources([...mergeSources, song]);
    }
    setMergePreview(null); // Reset preview when selection changes
  };
  
  // Set target (radio button)
  const setTarget = (song) => {
    // Remove from sources if it was there
    setMergeSources(mergeSources.filter(s => s.song_uid !== song.song_uid));
    setMergeTarget(song);
    setMergePreview(null);
  };
  
  // Load merge preview for multi-source
  const loadMultiMergePreview = async () => {
    if (!mergeTarget || mergeSources.length === 0) {
      setError('Επιλέξτε τουλάχιστον 1 πηγή και 1 στόχο');
      return;
    }
    
    setLoadingMerge(true);
    setError('');
    
    try {
      // Calculate totals from all sources
      let totalPlays = 0;
      let totalAliases = 0;
      
      for (const source of mergeSources) {
        const response = await axios.post(
          `${BACKEND_URL}/api/stream-monitor/song-identity/merge/preview`,
          { source_uid: source.song_uid, target_uid: mergeTarget.song_uid },
          getAuthHeaders()
        );
        totalPlays += response.data.source?.plays_count || 0;
        totalAliases += response.data.source?.aliases_count || 0;
      }
      
      // Get target info
      const targetResponse = await axios.post(
        `${BACKEND_URL}/api/stream-monitor/song-identity/merge/preview`,
        { source_uid: mergeSources[0].song_uid, target_uid: mergeTarget.song_uid },
        getAuthHeaders()
      );
      
      setMergePreview({
        sources_count: mergeSources.length,
        sources_plays: totalPlays,
        sources_aliases: totalAliases,
        target: targetResponse.data.target,
        after_merge: {
          total_plays: totalPlays + (targetResponse.data.target?.plays_count || 0),
          total_aliases: totalAliases + (targetResponse.data.target?.aliases_count || 0)
        }
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading merge preview');
    } finally {
      setLoadingMerge(false);
    }
  };
  
  // Execute multi-merge
  const executeMultiMerge = async () => {
    if (!mergeTarget || mergeSources.length === 0) return;
    
    const sourcesList = mergeSources.map(s => s.display).join('\n  • ');
    const confirmMsg = `Θέλετε σίγουρα να ενώσετε ${mergeSources.length} τραγούδια;\n\n` +
      `📤 ΠΗΓΕΣ (θα απορροφηθούν):\n  • ${sourcesList}\n\n` +
      `📥 ΣΤΟΧΟΣ (θα διατηρηθεί):\n  • ${mergeTarget.display}\n\n` +
      `⚠️ Αυτή η ενέργεια δεν μπορεί να αναιρεθεί εύκολα!`;
    
    if (!window.confirm(confirmMsg)) return;
    
    setLoadingMerge(true);
    setError('');
    
    let totalAliasesUpdated = 0;
    let totalPlaysUpdated = 0;
    let totalCanonicalsMerged = 0;
    let errors = [];
    
    try {
      // Merge each source into target
      for (const source of mergeSources) {
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/stream-monitor/song-identity/merge/execute`,
            {
              source_uid: source.song_uid,
              target_uid: mergeTarget.song_uid,
              target_display_artist: customDisplayArtist || null,
              target_display_title: customDisplayTitle || null
            },
            getAuthHeaders()
          );
          
          totalAliasesUpdated += response.data.aliases_updated || 0;
          totalPlaysUpdated += response.data.plays_updated || 0;
          totalCanonicalsMerged += response.data.canonical_records_merged || 0;
        } catch (err) {
          errors.push(`${source.display}: ${err.response?.data?.detail || err.message}`);
        }
      }
      
      setMergeSuccess({
        sources_merged: mergeSources.length - errors.length,
        aliases_updated: totalAliasesUpdated,
        plays_updated: totalPlaysUpdated,
        canonical_records_merged: totalCanonicalsMerged,
        errors: errors
      });
      setMergePreview(null);
      
      // Refresh stats and search results
      await fetchGlobalStats();
      if (searchQuery) {
        handleSearch();
      }
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Error executing merge');
    } finally {
      setLoadingMerge(false);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('el-GR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            🎵 Song Identity Manager
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Προβολή, αναζήτηση και ένωση canonical song identities
          </p>
        </div>
        <div className="flex gap-2">
          {!mergeMode ? (
            <button
              onClick={startMergeMode}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition flex items-center gap-2"
            >
              🔀 Merge Τραγούδια
            </button>
          ) : (
            <button
              onClick={cancelMerge}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              ✕ Ακύρωση Merge
            </button>
          )}
          <button
            onClick={fetchGlobalStats}
            disabled={loadingStats}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition"
          >
            🔄 Ανανέωση
          </button>
        </div>
      </div>

      {/* MERGE MODE PANEL - Multi-source support */}
      {mergeMode && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-500 rounded-lg p-6">
          <h3 className="text-lg font-bold text-orange-800 dark:text-orange-300 mb-4 flex items-center gap-2">
            🔀 Multi-Merge Mode - Ένωση Πολλαπλών Τραγουδιών
          </h3>
          
          <p className="text-sm text-orange-700 dark:text-orange-400 mb-4">
            <strong>Βήμα 1:</strong> Αναζητήστε τραγούδια • 
            <strong>Βήμα 2:</strong> Επιλέξτε ☑️ τις ΠΗΓΕΣ (θα απορροφηθούν) • 
            <strong>Βήμα 3:</strong> Επιλέξτε ◉ τον ΣΤΟΧΟ (θα διατηρηθεί)
          </p>
          
          {/* Selection Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Sources (will be absorbed) */}
            <div className={`p-4 rounded-lg border-2 ${mergeSources.length > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-400' : 'bg-gray-100 dark:bg-gray-800 border-dashed border-gray-400'}`}>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                ☑️ Πηγές ({mergeSources.length} επιλεγμένες)
                <span className="text-red-500">(θα απορροφηθούν)</span>
              </div>
              {mergeSources.length > 0 ? (
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {mergeSources.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-red-100 dark:bg-red-900/30 p-2 rounded">
                      <span className="truncate flex-1">{s.display}</span>
                      <button 
                        onClick={() => toggleSource(s)}
                        className="ml-2 text-red-600 hover:text-red-800"
                      >✕</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 italic text-sm">Επιλέξτε ☑️ τραγούδια από τα αποτελέσματα...</div>
              )}
            </div>
            
            {/* Target (will be kept) */}
            <div className={`p-4 rounded-lg border-2 ${mergeTarget ? 'bg-green-50 dark:bg-green-900/20 border-green-400' : 'bg-gray-100 dark:bg-gray-800 border-dashed border-gray-400'}`}>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                ◉ Στόχος 
                <span className="text-green-500">(θα διατηρηθεί)</span>
              </div>
              {mergeTarget ? (
                <div>
                  <div className="font-bold text-gray-900 dark:text-white">{mergeTarget.display}</div>
                  <div className="text-xs text-gray-500">{mergeTarget.song_uid}</div>
                  <button 
                    onClick={() => setMergeTarget(null)}
                    className="mt-2 text-sm text-green-600 hover:text-green-800"
                  >
                    ✕ Αφαίρεση
                  </button>
                </div>
              ) : (
                <div className="text-gray-500 italic text-sm">Επιλέξτε ◉ στόχο από τα αποτελέσματα...</div>
              )}
            </div>
          </div>
          
          {/* Preview Button */}
          {mergeSources.length > 0 && mergeTarget && !mergePreview && (
            <button
              onClick={loadMultiMergePreview}
              disabled={loadingMerge}
              className="w-full mb-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center justify-center gap-2"
            >
              {loadingMerge ? '⏳ Φόρτωση...' : '🔍 Preview Ένωσης'}
            </button>
          )}
          
          {/* Merge Preview */}
          {mergePreview && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4 border border-gray-200 dark:border-gray-700">
              <h4 className="font-bold text-gray-900 dark:text-white mb-3">📊 Preview Ένωσης</h4>
              
              <div className="grid grid-cols-3 gap-4 text-center mb-4">
                <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{mergePreview.sources_count}</div>
                  <div className="text-xs text-gray-500">Πηγές</div>
                  <div className="text-sm text-red-500">{mergePreview.sources_plays} plays</div>
                </div>
                <div className="flex items-center justify-center">
                  <span className="text-3xl">→</span>
                </div>
                <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{mergePreview.after_merge?.total_plays || 0}</div>
                  <div className="text-xs text-gray-500">Συνολικά Plays</div>
                  <div className="text-sm text-green-500">{mergePreview.after_merge?.total_aliases || 0} aliases</div>
                </div>
              </div>
              
              {/* Custom Display Name */}
              <div className="bg-gray-50 dark:bg-gray-900/30 p-4 rounded-lg mb-4">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  📝 Ορίστε πώς θα γράφεται (προαιρετικό)
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Καλλιτέχνης (π.χ. Θοδωρής Φέρρης)"
                    value={customDisplayArtist}
                    onChange={(e) => setCustomDisplayArtist(e.target.value)}
                    className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  />
                  <input
                    type="text"
                    placeholder="Τίτλος (π.χ. Είπες)"
                    value={customDisplayTitle}
                    onChange={(e) => setCustomDisplayTitle(e.target.value)}
                    className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>
              
              {/* Execute Button */}
              <button
                onClick={executeMultiMerge}
                disabled={loadingMerge}
                className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition disabled:bg-gray-400 flex items-center justify-center gap-2"
              >
                {loadingMerge ? (
                  <>⏳ Εκτέλεση...</>
                ) : (
                  <>🔀 Ένωση {mergeSources.length} Τραγουδιών → 1</>
                )}
              </button>
            </div>
          )}
          
          {/* Merge Success */}
          {mergeSuccess && (
            <div className="bg-green-100 dark:bg-green-900/30 border border-green-500 rounded-lg p-4">
              <div className="font-bold text-green-800 dark:text-green-300 mb-2">✅ Ένωση Επιτυχής!</div>
              <div className="text-sm text-green-700 dark:text-green-400">
                <div>• {mergeSuccess.sources_merged} πηγές ενώθηκαν</div>
                <div>• {mergeSuccess.aliases_updated} aliases ενημερώθηκαν</div>
                <div>• {mergeSuccess.plays_updated} plays μεταφέρθηκαν</div>
              </div>
              {mergeSuccess.errors?.length > 0 && (
                <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                  ⚠️ Σφάλματα: {mergeSuccess.errors.join(', ')}
                </div>
              )}
              <button
                onClick={cancelMerge}
                className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                ✓ Τέλος
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Global Stats Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          📊 Song Identity Stats
        </h3>
        
        {loadingStats ? (
          <div className="text-center py-4 text-gray-500">Φόρτωση...</div>
        ) : globalStats ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {globalStats.unique_song_uids?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Unique Songs</div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {globalStats.certified_display_names?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Certified Names</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                {globalStats.coverage_percent || '0'}%
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Coverage</div>
            </div>
            <div className={`rounded-lg p-4 ${globalStats.missing_song_uid > 0 ? 'bg-red-50 dark:bg-red-900/30' : 'bg-green-50 dark:bg-green-900/30'}`}>
              <div className={`text-3xl font-bold ${globalStats.missing_song_uid > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                {globalStats.missing_song_uid?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Missing song_uid</div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Backfill Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          🔧 Backfill Missing song_uid
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Δημιουργία song_uid για παλιά records που δεν έχουν. Πρώτα κάντε "Preview" για να δείτε πόσα θα επηρεαστούν.
        </p>
        
        <div className="flex gap-3 mb-4">
          <button
            onClick={() => handleBackfill(true)}
            disabled={loadingBackfill}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition"
          >
            {loadingBackfill ? '⏳ Processing...' : '👁️ Preview (Dry Run)'}
          </button>
          <button
            onClick={() => handleBackfill(false)}
            disabled={loadingBackfill}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition"
          >
            ▶️ Execute Backfill
          </button>
        </div>
        
        {backfillResult && (
          <div className={`p-4 rounded-lg ${backfillResult.action === 'dry_run' ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-green-50 dark:bg-green-900/30'}`}>
            <div className="text-sm font-medium mb-2">
              {backfillResult.action === 'dry_run' ? '📋 Preview Result' : '✅ Backfill Complete'}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Checked:</span>{' '}
                <span className="font-medium">{backfillResult.total_checked}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Missing UID:</span>{' '}
                <span className="font-medium text-orange-600">{backfillResult.missing_uid}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Updated:</span>{' '}
                <span className="font-medium text-green-600">{backfillResult.updated}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Errors:</span>{' '}
                <span className={`font-medium ${backfillResult.errors > 0 ? 'text-red-600' : ''}`}>
                  {backfillResult.errors}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confidence Gate Info Banner — replaces ex-cron jobs notice */}
      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300 dark:border-emerald-700 rounded-lg p-4 text-sm text-emerald-800 dark:text-emerald-200" data-testid="confidence-gate-banner">
        <div className="font-semibold mb-1">🛡️ Confidence Gate v1.6 — Proactive Ingestion</div>
        <ul className="list-disc list-inside space-y-0.5 text-xs">
          <li>Κάθε νέα εγγραφή αξιολογείται ντετερμινιστικά (0-100) <strong>στο ingestion</strong> πριν αποθηκευτεί.</li>
          <li>Jingles / URLs / station-IDs / programming-blocks απορρίπτονται αυτόματα στο gate.</li>
          <li>Variants (Greeklish/Greek caps) συγχωνεύονται μέσω canonical_alias_match.</li>
        </ul>
        <div className="mt-2 text-[11px] italic opacity-80">
          Τα παλιά retroactive cron jobs (Canonical Healer, Jingle Auto-Cleanup, Auto-Merge Near-Duplicates) έχουν αποσυρθεί — το Gate τα καλύπτει στην ώρα του ingestion.
        </div>
      </div>


      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          🔍 Αναζήτηση
        </h3>
        
        <form onSubmit={handleSearch} className="flex gap-3 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Αναζήτηση με artist, title ή alias..."
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            data-testid="song-identity-search-input"
          />
          <button
            type="submit"
            disabled={loadingSearch || searchQuery.length < 2}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 
                       disabled:bg-gray-400 transition"
            data-testid="song-identity-search-btn"
          >
            {loadingSearch ? '⏳' : '🔍 Αναζήτηση'}
          </button>
        </form>

        {/* Search Results */}
        {searchResults && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Βρέθηκαν <span className="font-medium text-gray-900 dark:text-white">{searchResults.unique_songs}</span> τραγούδια 
              ({searchResults.total_matches} aliases)
              {mergeMode && <span className="ml-2 text-orange-600 font-medium">(☑️ = Πηγή, ◉ = Στόχος)</span>}
            </div>
            
            {searchResults.results?.length > 0 ? (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {searchResults.results.map((song) => {
                  const songDisplay = `${song.aliases?.[0]?.raw_artist || 'Unknown'} - ${song.aliases?.[0]?.raw_title || 'Unknown'}`;
                  const isSelected = selectedSong?.song_uid === song.song_uid;
                  const isSource = mergeSources.some(s => s.song_uid === song.song_uid);
                  const isTarget = mergeTarget?.song_uid === song.song_uid;
                  
                  return (
                    <div 
                      key={song.song_uid}
                      className={`p-3 rounded-lg border transition
                        ${isSelected 
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30' 
                          : isSource
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/30'
                          : isTarget
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30'}`}
                      data-testid={`song-result-${song.song_uid}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {/* Merge Mode Controls */}
                          {mergeMode && (
                            <div className="flex items-center gap-3 mb-2">
                              {/* Checkbox for Source */}
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isSource}
                                  disabled={isTarget}
                                  onChange={() => toggleSource({ song_uid: song.song_uid, display: songDisplay })}
                                  className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                                />
                                <span className={`text-xs ${isSource ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                  Πηγή
                                </span>
                              </label>
                              
                              {/* Radio for Target */}
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="radio"
                                  name="merge-target"
                                  checked={isTarget}
                                  onChange={() => setTarget({ song_uid: song.song_uid, display: songDisplay })}
                                  className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                                />
                                <span className={`text-xs ${isTarget ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                                  Στόχος
                                </span>
                              </label>
                            </div>
                          )}
                          
                          <div 
                            className={`font-medium text-gray-900 dark:text-white ${!mergeMode ? 'cursor-pointer hover:text-purple-600' : ''}`}
                            onClick={() => {
                              if (!mergeMode) {
                                fetchSongDetails(song.song_uid);
                              }
                            }}
                          >
                            {songDisplay}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            song_uid: <span className="font-mono">{song.song_uid}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isSource && (
                            <span className="px-2 py-1 bg-red-500 text-white text-xs rounded">☑️ Πηγή</span>
                          )}
                          {isTarget && (
                            <span className="px-2 py-1 bg-green-500 text-white text-xs rounded">◉ Στόχος</span>
                          )}
                          <div className="text-sm text-purple-600 dark:text-purple-400">
                            {song.aliases?.length || 0} alias{song.aliases?.length !== 1 ? 'es' : ''}
                          </div>
                        </div>
                      </div>
                      
                      {/* Show all aliases if more than one */}
                      {song.aliases?.length > 1 && (
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-medium">Aliases:</span>
                          {song.aliases.map((alias, i) => (
                            <span key={i} className="ml-2 px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">
                              {alias.raw_artist} - {alias.raw_title}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Show display variations (Title Case, etc.) */}
                      {song.display_variations?.length > 0 && (
                        <div className="mt-2 text-xs">
                          <span className="font-medium text-cyan-600 dark:text-cyan-400">Προτεινόμενες μορφές:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {song.display_variations.map((v, i) => (
                              <span 
                                key={i} 
                                className="px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded cursor-pointer hover:bg-cyan-200 dark:hover:bg-cyan-800"
                                title={`Πηγή: ${v.source} - Κλικ για αντιγραφή`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(v.display);
                                }}
                              >
                                {v.display}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                Δεν βρέθηκαν αποτελέσματα για "{searchQuery}"
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected Song Details */}
      {selectedSong && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              📝 Λεπτομέρειες Τραγουδιού
            </h3>
            <button
              onClick={() => setSelectedSong(null)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>
          
          {loadingSongDetails ? (
            <div className="text-center py-4 text-gray-500">Φόρτωση...</div>
          ) : (
            <div className="space-y-4">
              {/* Main Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-3">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Song UID</div>
                  <div className="font-mono text-sm text-gray-900 dark:text-white">
                    {selectedSong.song_uid}
                  </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Total Plays</div>
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {selectedSong.total_plays?.toLocaleString() || 0}
                  </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Aliases</div>
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {selectedSong.alias_count || 0}
                  </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Date Range</div>
                  <div className="text-xs text-gray-700 dark:text-gray-300">
                    {formatDate(selectedSong.first_seen)}
                    <br />→ {formatDate(selectedSong.last_seen)}
                  </div>
                </div>
              </div>

              {/* Aliases Table */}
              {selectedSong.aliases?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Aliases & Play Counts
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Artist</th>
                          <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Title</th>
                          <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Source</th>
                          <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">Plays</th>
                          <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300">Stations</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {selectedSong.aliases.map((alias, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                            <td className="px-3 py-2 text-gray-900 dark:text-white">
                              {alias.raw_artist || '-'}
                            </td>
                            <td className="px-3 py-2 text-gray-900 dark:text-white">
                              {alias.raw_title || '-'}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs capitalize
                                ${alias.source === 'metadata' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                  alias.source === 'fingerprint' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                  alias.source === 'manual' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
                                {alias.source || 'unknown'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-white">
                              {alias.play_count?.toLocaleString() || 0}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                              {alias.stations?.slice(0, 3).join(', ') || '-'}
                              {alias.stations?.length > 3 && ` +${alias.stations.length - 3}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recent Aliases */}
      {globalStats?.recent_aliases?.length > 0 && !searchResults && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            🕐 Πρόσφατα Aliases
          </h3>
          <div className="space-y-2">
            {globalStats.recent_aliases.map((alias, idx) => (
              <div 
                key={idx}
                className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg
                           hover:bg-gray-100 dark:hover:bg-gray-900/50 cursor-pointer transition"
                onClick={() => fetchSongDetails(alias.song_uid)}
              >
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {alias.raw_artist} - {alias.raw_title}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {alias.song_uid} • {alias.source} • {formatDate(alias.created_at)}
                  </div>
                </div>
                <span className="text-purple-600 dark:text-purple-400">→</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SongIdentityManager;
